/**
 * ChatMessageBubble — renders a BubbleMessage as one of:
 *   plain (AI / User)  ·  thinking  ·  contradiction  ·  recap  ·  system
 *
 * Phase 3.2: visual port of design/kaiva-canvas/project/components/chat.jsx
 * with v1.1 brand tokens. Backend wiring of thinking / contradiction /
 * recap events lives in Phase 3.3.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, Check, Quote, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { TYPE_META, isNodeType } from '@/lib/nodeTypes';
import { KaivaAvatar } from '@/components/brand';
import {
  formatBubbleTime,
  type BubbleMessage,
  type ContradictionOption,
  type RecapOption,
  type ThoughtChip,
} from './bubbleTypes';

import type { FixtureNode } from './__fixtures__/bubbleFixture';

export type ChatBubbleNode = FixtureNode | { id: string; type: string; title: string };

interface ChatMessageBubbleProps {
  msg: BubbleMessage;
  /** Map of node id → minimal node info for RefChip rendering. Pass `{}` if unwired. */
  nodesById?: Record<string, ChatBubbleNode>;
  onChipClick?: (nodeId: string) => void;
  onPick?: (option: ContradictionOption | RecapOption) => void;
}

export function ChatMessageBubble({
  msg,
  nodesById = {},
  onChipClick,
  onPick,
}: ChatMessageBubbleProps) {
  switch (msg.kind) {
    case 'thinking':
      return <ThinkingBubble thoughts={msg.thoughts} />;
    case 'contradiction':
      return (
        <ContradictionBubble
          msg={msg}
          nodesById={nodesById}
          onChipClick={onChipClick}
          onPick={onPick}
        />
      );
    case 'recap':
      return (
        <RecapBubble
          msg={msg}
          nodesById={nodesById}
          onChipClick={onChipClick}
          onPick={onPick}
        />
      );
    case 'system':
      return <SystemNote text={msg.text} />;
    case 'plain':
      return msg.role === 'INTERVIEWER' ? (
        <AiMessage msg={msg} nodesById={nodesById} onChipClick={onChipClick} />
      ) : (
        <UserMessage msg={msg} />
      );
  }
}

// ─── Shared ──────────────────────────────────────────────────────────────────

function MetaRow({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'inline-flex flex-wrap items-center gap-1.5 px-0.5 kv-eyebrow text-[10.5px]',
        className,
      )}
      style={{ color: 'hsl(var(--muted-foreground))' }}
    >
      {children}
    </div>
  );
}

function MsgRow({ children, align = 'start' }: { children: ReactNode; align?: 'start' | 'end' }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      className={cn(
        'flex w-full max-w-[92%] flex-col gap-1',
        align === 'end' ? 'ml-auto items-end' : 'items-start',
      )}
    >
      {children}
    </motion.div>
  );
}

function RefChip({
  node,
  dim = false,
  onClick,
}: {
  node?: ChatBubbleNode;
  dim?: boolean;
  onClick?: (id: string) => void;
}) {
  if (!node) return null;
  const colorHsl = isNodeType(node.type) ? TYPE_META[node.type].color : '213 18% 38%';
  return (
    <button
      type="button"
      onClick={() => onClick?.(node.id)}
      className={cn(
        'inline-flex max-w-full items-center gap-1.5 truncate rounded-full px-2 py-[3px] text-[11px] transition-colors',
        dim ? 'opacity-70' : '',
      )}
      style={{
        background: 'hsl(var(--card))',
        border: '1px solid hsl(var(--border))',
        color: 'hsl(var(--foreground))',
      }}
    >
      <span
        className="inline-block h-[7px] w-[7px] flex-shrink-0 rounded-[2px]"
        style={{ background: `hsl(${colorHsl})` }}
      />
      <span className="truncate">{node.title}</span>
    </button>
  );
}

// ─── Thinking ────────────────────────────────────────────────────────────────

function ThinkingBubble({ thoughts }: { thoughts: ThoughtChip[] }) {
  const safeThoughts: ThoughtChip[] = thoughts.length > 0 ? thoughts : [{ text: 'Thinking…' }];
  const [i, setI] = useState(0);

  useEffect(() => {
    if (safeThoughts.length <= 1) return;
    const t = window.setInterval(
      () => setI((v) => Math.min(v + 1, safeThoughts.length - 1)),
      1100,
    );
    return () => window.clearInterval(t);
  }, [safeThoughts.length]);

  const cur = safeThoughts[i] ?? safeThoughts[0];

  return (
    <MsgRow>
      <div className="flex items-start gap-2.5">
        <KaivaAvatar size={22} pulse />
        <div className="flex min-w-0 flex-col gap-1">
          <MetaRow>Kaiva · thinking</MetaRow>
          <div
            className="inline-flex max-w-full items-center gap-2 rounded-xl rounded-tl-[4px] px-3 py-2.5 text-[13px]"
            style={{
              background: 'hsl(var(--muted))',
              color: 'hsl(var(--foreground))',
              letterSpacing: '-0.003em',
            }}
          >
            <span
              className="think-pulse inline-grid h-[22px] w-[22px] flex-shrink-0 place-items-center rounded-[7px]"
              style={{ background: 'hsl(var(--sienna) / 0.15)', color: 'hsl(var(--sienna-deep))' }}
            >
              <Sparkles className="h-3 w-3" />
            </span>
            <span
              key={i}
              className="think-slide inline-block italic"
              style={{ color: 'hsl(var(--foreground) / 0.78)' }}
            >
              {cur.text}
            </span>
            <span className="ml-0.5 inline-flex flex-shrink-0 gap-[3px]">
              {[0, 1, 2].map((k) => (
                <span
                  key={k}
                  className="typing-dot inline-block h-1 w-1 rounded-full"
                  style={{
                    background: 'hsl(var(--muted-foreground))',
                    animationDelay: `${k * 0.15}s`,
                  }}
                />
              ))}
            </span>
          </div>
          {safeThoughts.length > 1 && (
            <div className="mt-1 flex gap-[3px] pl-1">
              {safeThoughts.map((_, j) => (
                <span
                  key={j}
                  className="h-[2px] flex-1 rounded-[1px] transition-[background] duration-300"
                  style={{
                    maxWidth: 32,
                    background:
                      j <= i ? 'hsl(var(--sienna))' : 'hsl(var(--border))',
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </MsgRow>
  );
}

// ─── Contradiction ───────────────────────────────────────────────────────────

function ContradictionBubble({
  msg,
  nodesById,
  onChipClick,
  onPick,
}: {
  msg: Extract<BubbleMessage, { kind: 'contradiction' }>;
  nodesById: Record<string, ChatBubbleNode>;
  onChipClick?: (id: string) => void;
  onPick?: (option: ContradictionOption) => void;
}) {
  const earlierNode = msg.earlier.refId ? nodesById[msg.earlier.refId] : undefined;
  const nowNode = msg.now.refId ? nodesById[msg.now.refId] : undefined;

  return (
    <MsgRow>
      <div className="flex items-start gap-2.5">
        <KaivaAvatar size={22} />
        <div className="flex min-w-0 flex-col gap-1">
          <MetaRow>
            <span
              className="inline-flex items-center gap-1 rounded-full px-1.5 py-[2px] text-[9.5px] font-semibold"
              style={{ background: 'hsl(38 92% 92%)', color: 'hsl(38 92% 32%)' }}
            >
              <AlertTriangle className="h-[9px] w-[9px]" /> spotted a tension
            </span>
            <span>· Kaiva · {formatBubbleTime(msg.createdAt)}</span>
          </MetaRow>

          <div
            className="flex flex-col gap-2.5 rounded-xl rounded-tl-[4px] px-3.5 pb-3 pt-3"
            style={{
              background: 'hsl(var(--background))',
              border: '1px solid hsl(38 92% 80%)',
              borderLeft: '3px solid hsl(38 92% 55%)',
              boxShadow: 'var(--shadow-1)',
            }}
          >
            {/* Quote pair */}
            <div className="relative flex flex-col gap-1.5 pl-2.5">
              <span
                aria-hidden="true"
                className="absolute left-0 top-1 bottom-1 w-[1.5px] rounded-[1px]"
                style={{
                  background:
                    'linear-gradient(to bottom, hsl(var(--muted-foreground) / 0.4), hsl(38 92% 55%))',
                }}
              />
              <ContradictionRow when="earlier" quote={msg.earlier.quote} />
              <ContradictionRow when="now" quote={msg.now.quote} />
            </div>

            {/* Reference chips */}
            {(earlierNode || nowNode) && (
              <div className="flex flex-wrap gap-1.5">
                {earlierNode && (
                  <RefChip node={earlierNode} dim onClick={onChipClick} />
                )}
                {nowNode && <RefChip node={nowNode} onClick={onChipClick} />}
              </div>
            )}

            {/* The question */}
            <div
              className="text-[13.5px] font-medium leading-[1.5]"
              style={{ color: 'hsl(var(--foreground))', letterSpacing: '-0.005em' }}
            >
              {msg.question}
            </div>

            {/* Options */}
            <div className="flex flex-col gap-1.5">
              {msg.options.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => onPick?.(o)}
                  className="flex items-center justify-between gap-2.5 rounded-lg px-3 py-2 text-left transition-all duration-150 hover:translate-x-[2px]"
                  style={{
                    background: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                  }}
                >
                  <span
                    className="text-[12.5px] font-semibold"
                    style={{ color: 'hsl(var(--foreground))' }}
                  >
                    {o.label}
                  </span>
                  {o.detail && (
                    <span
                      className="ml-2 flex-shrink-0 text-right text-[11.5px]"
                      style={{ color: 'hsl(var(--muted-foreground))' }}
                    >
                      {o.detail}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </MsgRow>
  );
}

function ContradictionRow({ when, quote }: { when: 'earlier' | 'now'; quote: string }) {
  const isNow = when === 'now';
  return (
    <div className="flex items-start gap-2">
      <span
        className="mt-[1px] flex-shrink-0 rounded kv-eyebrow text-[9.5px] font-semibold"
        style={{
          minWidth: 48,
          padding: '3px 6px',
          textAlign: 'center',
          background: isNow ? 'hsl(38 92% 92%)' : 'hsl(var(--muted))',
          color: isNow ? 'hsl(38 92% 32%)' : 'hsl(var(--muted-foreground))',
        }}
      >
        {when}
      </span>
      <div
        className="flex items-start gap-1.5 text-[12.5px] italic leading-[1.45]"
        style={{
          color: isNow ? 'hsl(var(--foreground))' : 'hsl(var(--foreground) / 0.85)',
          fontWeight: isNow ? 500 : 400,
        }}
      >
        <Quote
          className="mt-[3px] h-[11px] w-[11px] flex-shrink-0"
          style={{ color: isNow ? 'hsl(38 92% 45%)' : 'hsl(var(--muted-foreground))' }}
        />
        <span>“{quote}”</span>
      </div>
    </div>
  );
}

// ─── Recap ───────────────────────────────────────────────────────────────────

function RecapBubble({
  msg,
  nodesById,
  onChipClick,
  onPick,
}: {
  msg: Extract<BubbleMessage, { kind: 'recap' }>;
  nodesById: Record<string, ChatBubbleNode>;
  onChipClick?: (id: string) => void;
  onPick?: (option: RecapOption) => void;
}) {
  const totalRefs = msg.sections.reduce(
    (n, s) => n + s.bullets.reduce((m, b) => m + (b.refs?.length ?? 0), 0),
    0,
  );

  return (
    <MsgRow>
      <div className="flex items-start gap-2.5">
        <KaivaAvatar size={22} />
        <div className="flex min-w-0 flex-col gap-1">
          <MetaRow>
            <span
              className="inline-flex items-center gap-1 rounded-full px-1.5 py-[2px] text-[9.5px] font-semibold"
              style={{
                background: 'hsl(var(--sienna-soft))',
                color: 'hsl(var(--sienna-deep))',
              }}
            >
              <Sparkles className="h-[9px] w-[9px]" /> recap
            </span>
            <span>· Kaiva · {formatBubbleTime(msg.createdAt)}</span>
          </MetaRow>

          <div
            className="recap-card flex flex-col gap-2.5 rounded-xl rounded-tl-[4px] px-3.5 pb-3 pt-3.5"
            style={{
              background: 'hsl(var(--background))',
              border: '1px solid hsl(var(--border-strong))',
              boxShadow: 'var(--shadow-1)',
            }}
          >
            {/* Header */}
            <div className="flex flex-wrap items-baseline justify-between gap-2.5">
              <div
                className="text-[14.5px] font-semibold"
                style={{ color: 'hsl(var(--foreground))', letterSpacing: '-0.01em' }}
              >
                {msg.title}
              </div>
              <div
                className="kv-eyebrow text-[10.5px]"
                style={{ color: 'hsl(var(--muted-foreground))' }}
              >
                <span className="font-semibold" style={{ color: 'hsl(var(--foreground))' }}>
                  {msg.sections.length}
                </span>{' '}
                areas ·{' '}
                <span className="font-semibold" style={{ color: 'hsl(var(--foreground))' }}>
                  {totalRefs}
                </span>{' '}
                concepts
              </div>
            </div>

            <div
              className="text-[12.5px] italic leading-[1.5]"
              style={{ color: 'hsl(var(--muted-foreground))' }}
            >
              {msg.intro}
            </div>

            {/* Sections */}
            <div className="mt-0.5 flex flex-col gap-2.5">
              {msg.sections.map((s, i) => (
                <div
                  key={i}
                  className="rounded-lg px-3 py-2.5"
                  style={{
                    background: 'hsl(var(--muted) / 0.5)',
                    border: '1px solid hsl(var(--border))',
                  }}
                >
                  <div
                    className="mb-1.5 flex items-center gap-2 kv-eyebrow text-[11px] font-semibold"
                    style={{ color: 'hsl(var(--foreground))' }}
                  >
                    <span
                      className="inline-grid h-[18px] w-[18px] place-items-center rounded-[5px] text-[9.5px] font-bold tracking-[0.02em]"
                      style={{
                        background: 'hsl(var(--sienna))',
                        color: 'hsl(var(--cream))',
                      }}
                    >
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    {s.cluster}
                  </div>
                  <ul className="m-0 flex list-none flex-col gap-1.5 p-0 pl-1">
                    {s.bullets.map((b, j) => (
                      <li key={j} className="relative flex flex-col gap-1 pl-3.5">
                        <span
                          aria-hidden="true"
                          className="absolute left-[1px] top-[8px] h-1.5 w-1.5 rounded-full"
                          style={{ background: 'hsl(var(--sienna))', opacity: 0.6 }}
                        />
                        <span
                          className="text-[12.5px] leading-[1.45]"
                          style={{ color: 'hsl(var(--foreground))', letterSpacing: '-0.003em' }}
                        >
                          {b.text}
                        </span>
                        {b.refs && b.refs.length > 0 && (
                          <div className="mt-0.5 flex flex-wrap gap-1">
                            {b.refs.map((id) => (
                              <RefChip key={id} node={nodesById[id]} onClick={onChipClick} />
                            ))}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {/* Options */}
            <div
              className="mt-1 flex flex-wrap gap-1.5 pt-2.5"
              style={{ borderTop: '1px solid hsl(var(--border))' }}
            >
              {msg.options.map((o) => {
                const isPrimary = o.kind === 'primary';
                return (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => onPick?.(o)}
                    className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[12px] font-medium transition-colors"
                    style={
                      isPrimary
                        ? {
                            background: 'hsl(var(--sienna))',
                            color: 'hsl(var(--cream))',
                            border: '1px solid hsl(var(--sienna))',
                          }
                        : {
                            background: 'hsl(var(--card))',
                            color: 'hsl(var(--foreground))',
                            border: '1px solid hsl(var(--border))',
                          }
                    }
                  >
                    {isPrimary && <Check className="h-2.5 w-2.5" />}
                    {o.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </MsgRow>
  );
}

// ─── Plain AI ────────────────────────────────────────────────────────────────

function AiMessage({
  msg,
  nodesById,
  onChipClick,
}: {
  msg: Extract<BubbleMessage, { kind: 'plain' }>;
  nodesById: Record<string, ChatBubbleNode>;
  onChipClick?: (id: string) => void;
}) {
  return (
    <MsgRow>
      <div className="flex items-start gap-2.5">
        <KaivaAvatar size={22} />
        <div className="flex min-w-0 flex-col gap-1">
          <MetaRow>Kaiva · {formatBubbleTime(msg.createdAt)}</MetaRow>
          <div
            className="rounded-xl rounded-tl-[4px] px-3 py-2.5 text-[13.5px] leading-[1.5]"
            style={{
              background: 'hsl(var(--muted))',
              color: 'hsl(var(--foreground))',
              letterSpacing: '-0.003em',
            }}
          >
            <p className="whitespace-pre-wrap">{msg.content}</p>
          </div>
          {msg.refs && msg.refs.length > 0 && (
            <div className="mt-0.5 flex flex-wrap gap-1">
              {msg.refs.map((id) => (
                <RefChip key={id} node={nodesById[id]} onClick={onChipClick} />
              ))}
            </div>
          )}
        </div>
      </div>
    </MsgRow>
  );
}

// ─── Plain User ──────────────────────────────────────────────────────────────

function UserMessage({ msg }: { msg: Extract<BubbleMessage, { kind: 'plain' }> }) {
  return (
    <MsgRow align="end">
      <MetaRow className="!justify-end">You · {formatBubbleTime(msg.createdAt)}</MetaRow>
      <div
        className="rounded-xl rounded-tr-[4px] px-3 py-2.5 text-[13.5px] leading-[1.5]"
        style={{
          background: 'hsl(var(--slate-navy))',
          color: 'hsl(var(--cream))',
          letterSpacing: '-0.003em',
        }}
      >
        <p className="whitespace-pre-wrap">{msg.content}</p>
      </div>
    </MsgRow>
  );
}

// ─── System note ─────────────────────────────────────────────────────────────

function SystemNote({ text }: { text: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      className="mx-auto inline-flex max-w-full items-center gap-1.5 self-center rounded-full px-3 py-1 text-[11.5px] italic"
      style={{
        background: 'hsl(var(--muted) / 0.6)',
        color: 'hsl(var(--muted-foreground))',
        border: '1px dashed hsl(var(--border))',
      }}
    >
      <Sparkles className="h-2.5 w-2.5 flex-shrink-0" />
      <span className="truncate">{text}</span>
    </motion.div>
  );
}
